<template>
	<div class="page page-stats">
		<div v-if="isLoading">
			<LoadingState />
		</div>
		<div class="container" v-if="!isLoading">
			<div class="section section-stats-history">
				<div class="filter-board filter">
					<form v-on:submit.prevent="onSearch">
						<div class="form-row">
							<div class="form-group col-md-3">
								<label for="channels">Channels</label>
								<select id="channels" class="form-control" v-model="filter.channelId">
									<option :value="null">All</option>
									<option v-for="(channel, index) in channels"
										:key="index"
										:value="channel._id">{{channel.name}}
									</option>
								</select>
							</div>
							<div class="form-group col-md-3">
								<label for="adsPreset">Ads Preset</label>
								<select id="adsPreset" class="form-control" v-model="filter.adsPresetId">
									<option :value="null">All</option>
									<option v-for="(ads, index) in adsPreset"
										:key="index"
										:value="ads._id">
										{{ads.name}}
									</option>
								</select>
							</div>
							<div class="form-group col-md-4 form-group-without-label">
								<label for="adsPreset">Date Range</label>
								<DateRangePicker
									:startDate="filter.startDate"
									:endDate="filter.endDate"
									:onChangeDate="onChangeDate" />
							</div>
							<div class="form-group col-md-2">
								<button type="submit"
									class="btn btn-outline-primary btn-search btn-block">
									Search
								</button>
							</div>
						</div>
					</form>
				</div>
				<div class="list-group" v-if="publishers.length > 0">
					<div class="list-header">
						<div class="total">
							<span class="d-none d-sm-block">
								{{pageData.from}} - {{(pageData.from + publishers.length) - 1}} from {{pageData.total}} items
							</span>
						</div>
						<div class="sort">
							<div class="label-sort">Sort by</div>
							<select class="custom-select custom-select-sm" v-model="filterSort">
								<option v-for="(item, index) in sortList" :value="item.value" :key="index">{{item.name}}</option>
							</select>
						</div>
					</div>
					<div class="list-item" v-for="(publisher, index) in publishers" :key="index">
						<div class="list-item-content list-item-date">
							<div class="sub-title">{{ getDateTime(publisher.publishedAt) }}</div>
						</div>
						<div class="list-item-content list-item-primary">
							<div class="title">{{publisher.adsPresetName}}</div>
							<div class="sub-title d-none d-sm-block" v-if="publisher.account">
								publsihed by {{publisher.account.name}}
							</div>
						</div>
						<div class="list-item-content d-none d-sm-block">
							<div class="sub-title">{{publisher.channelName}}</div>
						</div>
						<div class="list-item-content list-item-last">
							<div class="number">
								<span class="d-none d-sm-block">{{publisher.subscribers}}</span>
								<span class="d-block d-sm-none">{{prettyShortNumeral(publisher.subscribers)}}</span>
							</div>
							<div class="sub-title d-none d-sm-block">Subscribers</div>
						</div>
					</div>
				</div>
				<div class="list-group" v-if="publishers.length === 0">
					<div class="list-item">
						<div class="list-item-content list-item-empty">
							<div class="title text-center">No history found</div>
							<div class="sub-title text-center" v-if="hasFilter()">
								Please Change Filter or <a class="text-primary" @click="resetFilter()">Reset</a>
							</div>
						</div>
					</div>
				</div>
				<Pagination :lastPage="pageData.lastPage" v-if="pageData.lastPage > 1" />
			</div>
		</div>
	</div>
</template>

<script>
import _ from 'lodash';
import qs from 'qs';
import { getDateTime, prettyShortNumeral } from '@/modules/utilities';
import { loadChannels } from '@/modules/api/Channel';
import { loadAdsPreset } from '@/modules/api/AdsPreset';
import { loadPublishers } from '@/modules/api/Publisher';
import DateRangePicker from '@/components/elements/DateRangePicker';
import LoadingState from '@/components/elements/LoadingState';
import Pagination from '@/components/elements/Pagination';

export default {
	components: {
		DateRangePicker,
		LoadingState,
		Pagination,
	},
	data() {
		return {
			publishers: null,
			lastPage: 0,
			totalPublishers: 0,
			pageData: null,
			from: 0,
			isLoading: true,
			channels: [],
			adsPreset: [],
			filterSort: {
				order: 'desc',
				orderby: 'date',
			},
			filter: {
				channelId: null,
				adsPresetId: null,
				page: 1,
				startDate: null,
				endDate: null,
				order: 'desc',
				orderby: 'date',
			},
			sortList: [
				{
					name: 'Newest',
					value: {
						order: 'desc',
						orderby: 'date',
					},
				},
				{
					name: 'Oldest',
					value: {
						order: 'asc',
						orderby: 'date',
					},
				},
				{
					name: 'Subscribers High to Low',
					value: {
						order: 'desc',
						orderby: 'subscribers',
					},
				},
				{
					name: 'Subscribers Low to High',
					value: {
						order: 'asc',
						orderby: 'subscribers',
					},
				},
			],
		};
	},
	watch: {
		'$route.query': {
			handler() {
				const filter = this.getFilterFromQuery();
				this.load(filter);
			},
			deep: true,
		},
		filterSort: {
			handler() {
				// set to filter
				this.filter.order = this.filterSort.order;
				this.filter.orderby = this.filterSort.orderby;
				this.onSearch();
			},
			deep: true,
		},
	},
	created() {
		this.filter = _.defaults(this.getFilterFromQuery(), this.filter);
		this.setFilterSortFromFilter();
		this.load(this.filter);
	},
	methods: {
		getDateTime,
		prettyShortNumeral,
		hasFilter() {
			return _.some(this.filter, filter => filter !== null);
		},
		setFilterSortFromFilter() {
			this.filterSort.order = this.filter.order;
			this.filterSort.orderby = this.filter.orderby;
		},
		getFilterFromQuery() {
			return qs.parse(this.$route.query, { ignoreQueryPrefix: true, skipNulls: true });
		},
		async load(filter) {
			try {
				const channel = await loadChannels();
				const preset = await loadAdsPreset();

				this.channels = channel.data;
				this.adsPreset = preset.data;

				const response = await loadPublishers(filter);
				this.pageData = response.data;
				this.publishers = response.data.data;
				this.isLoading = false;
			} catch (e) {
				console.log(e);
			}
		},
		async onLoadPublishers(filter) {
			try {
				const response = await loadPublishers(filter);
				this.pageData = response.data;
				this.publishers = response.data.data;
				this.changeRoute(filter);
			} catch (e) {
				console.log(e);
			}
		},
		async onSearch() {
			const filter = { ...this.filter, page: 1 };
			this.onLoadPublishers(filter);
		},
		resetFilter() {
			const filter = { channelId: null, adsPresetId: null, page: 1, startDate: null, endDate: null };
			this.onLoadPublishers(filter);
		},
		changeRoute(filter) {
			const query = qs.stringify(filter, { addQueryPrefix: true, skipNulls: true, encode: false });
			this.$router.replace(query);
		},
		onChangeDate(dateRange) {
			this.filter.startDate = dateRange.startDate;
			this.filter.endDate = dateRange.endDate;
		},
	},
};
</script>

<style lang="scss" scoped>
</style>



// WEBPACK FOOTER //
// src/components/pages/history/HistoryDashBoard.vue